import {FC} from "react";
import {useState} from "react";
import {PDFDocument} from "pdf-lib";

const BRAND = "#2563eb";


type Ext = "pdf" | "txt" | "json" | "csv" | "jpg" | "png" | "bin" |"mp3";
const mimeMap: Record<Ext, string> = {
    pdf: "application/pdf",
    txt: "text/plain",
    json: "application/json",
    csv: "text/csv",
    jpg: "image/jpeg",
    png: "image/png",
    bin: "application/octet-stream",
    mp3: "application/mp3",
};

interface JPGToPDFConverterProps {
    onBack?: () => void;
}

const UniversalFileGenerator: FC<JPGToPDFConverterProps> = ({onBack}) => {
    const [ext, setExt] = useState<Ext>("pdf");
    const [size, setSize] = useState(1);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);

    const handleGenerate = async () => {
        setLoading(true);

        const f = await createTestFile("test_"+size+"MB", ext, size);

        setFile(f);
        setLoading(false);
    }
    const createTestFile = async (
        name: string,
        ext: Ext,
        sizeMB: number
    ): Promise<File> => {


        if (ext === "pdf") {
            return await createPdf(sizeMB, name);
        }

        return createGenericFile(ext, sizeMB, name);
    };

    const createGenericFile = (
        ext: Exclude<Ext, "pdf">,
        sizeMB: number,
        name: string
    ): File => {
        const mime = mimeMap[ext];

        // convert MB → bytes
        const sizeBytes = Math.floor(sizeMB * 1024 * 1024);

        let blob: Blob;

        // TEXT FILES
        if (ext === "txt" || ext === "json" || ext === "csv") {
            const base = "0".repeat(1024);

            const repeatCount = Math.ceil(sizeBytes / base.length);

            const content = new Array(repeatCount).fill(base).join("");

            blob = new Blob([content.slice(0, sizeBytes)], {type: mime});
        }

        // BINARY FILES (jpg/png/bin)
        else {
            const buffer = new Uint8Array(sizeBytes);

            for (let i = 0; i < sizeBytes; i++) {
                buffer[i] = Math.floor(Math.random() * 256);
            }

            blob = new Blob([buffer], {type: mime});
        }

        return new File([blob], `${name}.${ext}`, {type: mime});
    };


    const createPdf = async (
        sizeMB: number,
        name: string
    ): Promise<File> => {
        const targetBytes = Number(sizeMB) * 1024 * 1024;

        if (!Number.isFinite(targetBytes) || targetBytes <= 0) {
            throw new Error("Invalid file size");
        }

        if (targetBytes > 2000 * 1024 * 1024) {
            throw new Error("Maximum supported size is 100 MB");
        }
        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([600, 800]);

        page.drawText("Test PDF", {
            x: 50,
            y: 750,
            size: 20,
        });

        // Save ONCE
        const pdfBytes = await pdfDoc.save();

        let fileBytes = pdfBytes;

        // Pad to target size
        if (pdfBytes.length < targetBytes) {
            const padding = new Uint8Array(targetBytes - pdfBytes.length);

            fileBytes = new Uint8Array(targetBytes);
            fileBytes.set(pdfBytes, 0);
            fileBytes.set(padding, pdfBytes.length);
        }
        console.log("PDF generated successfully")

        return new File([fileBytes], `${name}.pdf`, {
            type: "application/pdf",
        });
    };
    const handleDownload = () => {
        if (!file) return;

        const url = URL.createObjectURL(file);
        const a = document.createElement("a");

        a.href = url;
        a.download = file.name;
        a.click();

        URL.revokeObjectURL(url);
    };

    return (
        <div style={{maxWidth: 900, margin: "0 auto", padding: "40px"}}>
            {/* Back Button */}
            {onBack && (
                <button
                    onClick={onBack}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#2563eb",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        marginBottom: 24,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        transition: "color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#1d4ed8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#2563eb")}
                >
                    ← Back to Home
                </button>
            )}

            <h1 style={{fontSize: 36, fontWeight: 700, marginBottom: 12, color: "#1a1a1a"}}>
                Generate Dummy Files
            </h1>
            <p style={{fontSize: 16, color: "#666", marginBottom: 32, lineHeight: 1.6}}>
                Generate Dummy File Download for Testing & Development
            </p>
            <select
                value={ext}
                onChange={(e) => setExt(e.target.value as Ext)}
                className="file-type-select"
            >
                <option value="pdf">PDF</option>
                <option value="txt">TXT</option>
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
                <option value="jpg">JPG</option>
                <option value="png">PNG</option>
                <option value="bin">BIN</option>
                <option value="mp3">MP3</option>
            </select>
            <div className="file-size-container">
                <input
                    type="number"
                    min={1}
                    placeholder="File size"
                    value={size || ""}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="file-size-unit"
                />
                <span className="file-size-unit">MB</span>
            </div>
            <div>
                 <button onClick={handleGenerate} disabled={loading} style={{
                        width: "100%",
                        background: BRAND,
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "14px 28px",
                        fontSize: 16,
                        fontWeight: 600,
                        transition: "background 0.15s",
                    }}>
                        {loading ? "Generating..." : "Generate File"}
                    </button>
            </div>
            {file && (
                <>
                    <p>Name: {file.name}</p>
                    <p>Size: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <button onClick={handleDownload} style={{
                        width: "100%",
                        background: "green",
                        color: "#fff",
                        border: "none",
                        borderRadius: 10,
                        padding: "14px 28px",
                        fontSize: 16,
                        fontWeight: 600,
                        transition: "background 0.15s",
                    }}>Download
                    </button>
                </>
            )}


            {/* Info Section */}
            <div
                style={{
                    background: "#f0f4ff",
                    border: `1px solid ${BRAND}`,
                    borderRadius: 12,
                    padding: 20,
                    marginTop: 32,
                }}
            >
                <h3 style={{fontSize: 14, fontWeight: 600, color: BRAND, marginBottom: 12}}>ℹ️ How it works</h3>
                <ul style={{fontSize: 13, color: "#555", lineHeight: 1.8, paddingLeft: 20, margin: 0}}>
                    <li>Select the extension</li>
                    <li>Provide the size</li>
                    <li>No file is stored on our servers - all conversions happen locally</li>
                </ul>
            </div>
        </div>
    );
};

export default UniversalFileGenerator;

